import classNames from 'clsx';
import {
  forwardRef,
  useMemo,
  useReducer,
  type ReactNode,
  Children,
} from 'react';
import { isElement } from 'react-is';

import * as styles from './FieldSet.sty.js';
import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { AriaDisabledProps } from '../../../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../../../core/types/data-props.js';
import { MaskingProps } from '../../../../core/types/masking-props.js';
import { StylingProps } from '../../../../core/types/styling-props.js';
import { WithChildren } from '../../../../core/types/with-children.js';
import { mergeProps } from '../../../../core/utils/merge-props.js';
import { parseBoolean as _parseBoolean } from '../../../../core/utils/parse-boolean.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { _FormFieldContext as FormFieldContext } from '../../contexts/FormFieldContext.js';
import { _formFieldReducer } from '../../state/formfield-state-reducer.js';

const COMPONENT_NAME = 'FieldSet';

/**
 * @public
 */
export interface FieldSetProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps,
    AriaDisabledProps,
    BehaviorTrackingProps {
  /**
   * Whether the fieldset and all its fields are disabled or not.
   * @defaultValue false
   */
  disabled?: boolean;
  /** The name associated with the group in a form. */
  name?: string;
}

const RootFieldSet = forwardRef<HTMLDivElement, FieldSetProps>(
  (props, forwardedRef) => {
    const {
      children,
      disabled: propDisabled,
      name,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'aria-disabled': ariaDisabledProp,
      ...remainingProps
    }: FieldSetProps = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const [formFieldState] = useReducer(_formFieldReducer, {});

    const slots = useMemo(() => getSlots(children), [children]);

    return (
      <FormFieldContext.Provider
        value={{
          disabled: formFieldState.disabled ?? propDisabled,
          ariaDisabled:
            formFieldState.ariaDisabled ?? _parseBoolean(ariaDisabledProp),
          required: formFieldState.required,
          dispatch: () => undefined,
        }}
      >
        <div
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          className={consumerClassName}
          style={consumerStyle}
          ref={forwardedRef}
          {...mergeProps(restProps, behavioralTrackingProps)}
          data-dt-component={COMPONENT_NAME}
        >
          <fieldset name={name} className={styles.fieldset}>
            {slots.legend}
            <Flex gap={12} flexDirection="column">
              {slots.rest}
            </Flex>
          </fieldset>
        </div>
      </FormFieldContext.Provider>
    );
  },
);
(RootFieldSet as typeof RootFieldSet & { displayName: string }).displayName =
  COMPONENT_NAME;

function getSlots(children: ReactNode) {
  let legend: ReactNode;
  const rest: ReactNode[] = [];

  for (const child of Children.toArray(children)) {
    if (isElement(child) && child.type === Legend) {
      legend = child;
    } else {
      rest.push(child);
    }
  }

  return { legend, rest };
}

/**
 * @public
 */
export interface FieldSetLegendProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    BehaviorTrackingProps {}

/**
 * Assigns a caption to the `FieldSet` to improve accessibility.
 */
const Legend = forwardRef<HTMLLegendElement, FieldSetLegendProps>(
  (props, ref) => {
    const { style, className, ...rest } = props;
    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(rest);
    return (
      <legend
        className={classNames(styles.legend, className)}
        style={style}
        ref={ref}
        {...mergeProps(restProps, behavioralTrackingProps)}
      >
        {props.children}
      </legend>
    );
  },
);
(Legend as typeof Legend & { displayName: string }).displayName =
  'FieldSet.Legend';

/**
 * Use `FieldSet` to semantically group related form fields.
 * @public
 */
export const FieldSet = Object.assign(RootFieldSet, {
  Legend,
});
